import React, { useMemo } from 'react';
import { Grid } from '@mui/material';
import { NO_DATA_TO_SHOW } from 'spw-constants';
import { useSelector } from 'react-redux';
import {
  selectProjectHasOnlyOneBuilding,
  selectTotalBuildingAndFloorCount,
} from 'spw-redux/modules/projects/selectors';
import {
  selectProgramSummaryBuildingsSelected,
  selectSelectedLevelsExpanded,
} from 'spw-redux/modules/program-summary/esa/selectors';
import { IProject } from 'spw-redux/modules/projects/types';
import { getAriaLabelForSensitiveInfo } from 'spw-shared/utils';
import { formatAddress } from 'spw-components/address-autocomplete/utils';
import { JustifyEndColumn } from 'spw-shared/globalComponents/grid';
import {
  ProjectName,
  SubHeader,
  Card,
  CardSubtitle,
} from '../../../programming/program-summary/macro-single-page-summary/styles';
import { ProjectOverviewCardTitle } from '../../../programming/program-summary/single-page-summary/styles';
import { useESASelectedFloorAndBuildingLabels } from '../floor-building-labels-hook';

export const ProjectOverview = ({ project }: { project: IProject }) => {
  const projectHasOnlyOneBuilding = useSelector(
    selectProjectHasOnlyOneBuilding
  );
  const { allSelectedFloorIds, allSelectedBuildingIds } = useSelector(
    selectSelectedLevelsExpanded
  );
  const selectedProgramSummaryBuildings = useSelector(
    selectProgramSummaryBuildingsSelected
  );
  const selectedBuildingsCount = useMemo(() => allSelectedBuildingIds.length, [
    allSelectedBuildingIds,
  ]);
  const [totalBuildingCount, totalFloorCount] = useSelector(
    selectTotalBuildingAndFloorCount
  );

  const selectedFloorsCount = useMemo(() => {
    return allSelectedFloorIds.length;
  }, [allSelectedFloorIds]);

  const { addressLabel, floorLabel } = useESASelectedFloorAndBuildingLabels();
  const context = 'Program Summary Project Overview';
  return (
    <Card key="summary-data" style={{ width: '100%' }}>
      <Grid container>
        <Grid item xs={6}>
          <JustifyEndColumn>
            <ProjectOverviewCardTitle data-testid="project_summary_title">
              Project Overview
            </ProjectOverviewCardTitle>
            <ProjectName
              data-testid="project_summary_project_name"
              aria-label={getAriaLabelForSensitiveInfo({
                context,
                desc: 'Project Name',
                id: project?.id,
              })}
            >
              {project.projectName}
            </ProjectName>{' '}
            <SubHeader
              data-testid="project_summary_building_address"
              className="no-padding"
              style={{ paddingBottom: '4px' }}
              aria-label={getAriaLabelForSensitiveInfo({
                context,
                desc: 'Project Location',
                id: project?.id,
              })}
            >
              {addressLabel}
            </SubHeader>
            <SubHeader style={{ height: '16px' }}>
              {selectedBuildingsCount === 1
                ? formatAddress(selectedProgramSummaryBuildings[0])
                : '--'}
            </SubHeader>
            <CardSubtitle data-testid="project_summary_client_name">
              {project?.client?.name ? project?.client?.name : NO_DATA_TO_SHOW}
            </CardSubtitle>
            <SubHeader className="label">Client Name</SubHeader>
            <CardSubtitle
              data-testid="project_summary_industry"
              aria-label={getAriaLabelForSensitiveInfo({
                context,
                desc: 'Client Name',
                id: project?.client?.id,
              })}
            >
              {project?.client?.industry?.name
                ? project?.client?.industry?.name
                : NO_DATA_TO_SHOW}
            </CardSubtitle>
            <SubHeader className="label">Industry</SubHeader>
            <CardSubtitle
              data-testid="project_summary_project_number"
              aria-label={getAriaLabelForSensitiveInfo({
                context,
                desc: 'Project Number',
                id: project?.id,
              })}
            >
              {project?.projectNumber
                ? project?.projectNumber
                : NO_DATA_TO_SHOW}
            </CardSubtitle>
            <SubHeader className="label">Project Number</SubHeader>
          </JustifyEndColumn>
        </Grid>
        <Grid item xs={6}>
          <JustifyEndColumn>
            {projectHasOnlyOneBuilding ? (
              ''
            ) : (
              <>
                <CardSubtitle data-testid="project_summary_building_quantity">
                  {selectedBuildingsCount} of {totalBuildingCount}
                </CardSubtitle>
                <SubHeader className="label">Quantity of Buildings</SubHeader>
              </>
            )}
            <CardSubtitle data-testid="project_summary_floor_quantity">
              {selectedFloorsCount} of {totalFloorCount}
            </CardSubtitle>
            <SubHeader className="label">Quantity of Floors</SubHeader>
            <CardSubtitle data-testid="project_summary_floor_name">
              {floorLabel}
            </CardSubtitle>
            <SubHeader className="label">Floor Name</SubHeader>
          </JustifyEndColumn>
        </Grid>
      </Grid>
    </Card>
  );
};
